/**
 * FormUI — Shared form element components for the Flow Studio designer.
 *
 * All components read `theme` from `useUIStore()` internally.
 * To change the look of every input, button, label, etc. across the app,
 * edit formUI.css and this file.
 *
 * Exports:
 *   FormGroup      – wrapper with bottom margin
 *   FormLabel      – styled <label>
 *   FormRow        – 2-column grid
 *   FormActions    – right-aligned button row
 *   FormError      – red error banner
 *   FormInfo       – green info banner
 *   Input          – text / email / number / color / password input
 *   Textarea       – multi-line input
 *   Select         – native <select> dropdown
 *   Checkbox       – checkbox + inline label
 *   Button         – variant: 'primary' | 'secondary' | 'danger' | 'ghost'
 */

import React from 'react';
import './formUI.css';

// ─── FormGroup ────────────────────────────────────────────────────────────────

export interface FormGroupProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const FormGroup: React.FC<FormGroupProps> = ({ className, style, children }) => (
  <div className={`form-group ${className || ''}`} style={style}>{children}</div>
);

// ─── FormLabel ────────────────────────────────────────────────────────────────

export interface FormLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
  inline?: boolean;
}

export const FormLabel: React.FC<FormLabelProps> = ({ required, inline, children, className, style, ...rest }) => {
  const labelClass = `form-label ${inline ? 'form-label--inline' : ''} ${className || ''}`.trim();
  return (
    <label className={labelClass} style={style} {...rest}>
      {children}
      {required && <span className="form-label__required">*</span>}
    </label>
  );
};

// ─── FormRow ──────────────────────────────────────────────────────────────────

export interface FormRowProps {
  columns?: number;
  gap?: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const FormRow: React.FC<FormRowProps> = ({ columns = 2, gap, className, style, children }) => {
  const columnClass = columns === 3 ? 'form-row--3-columns' : columns === 4 ? 'form-row--4-columns' : '';
  const rowClass = `form-row ${columnClass} ${className || ''}`.trim();

  const customStyle = gap ? { gap, ...style } : style;

  return (
    <div className={rowClass} style={customStyle}>
      {children}
    </div>
  );
};

// ─── FormActions ──────────────────────────────────────────────────────────────

export interface FormActionsProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const FormActions: React.FC<FormActionsProps> = ({ className, style, children }) => (
  <div className={`form-actions ${className || ''}`} style={style}>
    {children}
  </div>
);

// ─── FormError ────────────────────────────────────────────────────────────────

export interface FormErrorProps {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const FormError: React.FC<FormErrorProps> = ({ className, children, style }) => (
  <div className={`form-error ${className || ''}`} style={style}>
    {children}
  </div>
);

// ─── FormInfo ─────────────────────────────────────────────────────────────────

export interface FormInfoProps {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const FormInfo: React.FC<FormInfoProps> = ({ className, children, style }) => (
  <div className={`form-info ${className || ''}`} style={style}>
    {children}
  </div>
);

// ─── Input ────────────────────────────────────────────────────────────────────

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  fullWidth?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ fullWidth = true, className, style, ...rest }, ref) => {
    const colorClass = rest.type === 'color' ? 'form-input--color' : '';
    const inputClass = `form-input ${colorClass} ${className || ''}`.trim();
    const customStyle = !fullWidth ? { width: 'auto', ...style } : style;
    return <input ref={ref} className={inputClass} style={customStyle} {...rest} />;
  }
);

// ─── Textarea ─────────────────────────────────────────────────────────────────

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  fullWidth?: boolean;
  minHeight?: string;
}

export const Textarea: React.FC<TextareaProps> = ({
  fullWidth = true,
  minHeight = '80px',
  className,
  style,
  ...rest
}) => {
  const heightClass =
    minHeight === '100px'
      ? 'form-textarea--min-height-100'
      : minHeight === '120px'
      ? 'form-textarea--min-height-120'
      : '';

  const textareaClass = `form-textarea ${heightClass} ${className || ''}`.trim();

  const customStyle = {
    ...(minHeight !== '80px' && minHeight !== '100px' && minHeight !== '120px' ? { minHeight } : {}),
    ...(!fullWidth ? { width: 'auto' } : {}),
    ...style,
  };

  return <textarea className={textareaClass} style={Object.keys(customStyle).length > 0 ? customStyle : undefined} {...rest} />;
};

// ─── Select ───────────────────────────────────────────────────────────────────

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  placeholder?: string;
  fullWidth?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  options,
  placeholder,
  fullWidth = true,
  className,
  style,
  ...rest
}) => {
  const selectClass = `form-select ${className || ''}`.trim();
  const customStyle = !fullWidth ? { width: 'auto', ...style } : style;

  return (
    <select className={selectClass} style={customStyle} {...rest}>
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
};

// ─── Checkbox ─────────────────────────────────────────────────────────────────

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: React.ReactNode;
  htmlFor?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({ label, htmlFor, className, style, id, ...rest }) => {
  const inputId = htmlFor ?? id;
  const wrapperClass = `form-checkbox-wrapper ${className || ''}`.trim();

  return (
    <div className={wrapperClass} style={style}>
      <input id={inputId} type="checkbox" className="form-checkbox" {...rest} />
      <label htmlFor={inputId} className="form-checkbox-label">
        {label}
      </label>
    </div>
  );
};

// ─── Button ───────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  loadingText?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  loading = false,
  loadingText,
  children,
  className,
  style,
  disabled,
  ...rest
}) => {
  const variantClass = `form-button--${variant}`;
  const buttonClass = `form-button ${variantClass} ${className || ''}`.trim();

  return (
    <button className={buttonClass} disabled={disabled || loading} style={style} {...rest}>
      {loading ? (loadingText ?? 'Loading...') : children}
    </button>
  );
};
